/**
 * useStep4ThreatIdentification — Step 4 lifecycle.
 *
 * Composes the shared sub-flow hooks into Step 4's body-props contract:
 *   - `useStructuredFetch` × 3 — threats, assets (asset_id select),
 *     damage scenarios (damage_ids multi-select)
 *   - `useRecordCRUD`            — add / update / delete via the workflow
 *     record endpoints
 *   - `useHierarchicalApprove`   — child relevance + the approve closure
 *
 * Wire shape: `raw.result.threats_identified ?? raw.result.threats`
 * (outer wrapper has the standard timestamp / agent_name / extraction
 * keys). Hierarchical rows have `source: 'child_tara'` — default
 * `isChild` predicate of `useHierarchicalApprove` handles them.
 */
import { useCallback } from 'react';
import { getAgentResult } from '../../../api/workflow';
import { useStructuredFetch } from '../hooks/useStructuredFetch';
import { useRefetchOnReviseComplete } from '../hooks/useRefetchOnReviseComplete';
import { useRecordCRUD } from '../hooks/useRecordCRUDv2';
import { useHierarchicalApprove } from '../hooks/useHierarchicalApprove';
import type { Step4BodyProps } from '../step-components/stepBodyProps';
import type {
  PerStepState,
  WorkflowActions,
  WorkflowV2State,
} from '../state/workflowV2Types';
import type { OptionLite } from '../step-components/step4-threats/ThreatFormModal';

const HAS_OUTPUT = new Set(['draft', 'revising', 'approved']);
const AGENT_NAME = 'threat_analysis_agent';
const ASSET_AGENT_NAME = 'asset_identification_agent';
const SCENARIO_AGENT_NAME = 'damage_scenario_agent';

interface AgentResultResponse {
  result?: Record<string, unknown>;
  [key: string]: unknown;
}

interface AssetLite {
  asset_id?: string;
  asset_name?: string;
  [key: string]: unknown;
}

interface ScenarioLite {
  damage_id?: string;
  damage_scenario?: string;
  [key: string]: unknown;
}

export interface UseStep4Input {
  threadId: string | null | undefined;
  stepState: PerStepState | undefined;
  workflowCompleted: boolean;
  analysisMode?: string | null;
  /** Reducer state — needed so `useHierarchicalApprove` can read
   *  `state.steps[4].hierarchicalModifications` (Story H2). */
  state: WorkflowV2State;
  /** Action creators bound to the reducer's dispatch. */
  actions: WorkflowActions;
}

export function useStep4ThreatIdentification({
  threadId,
  stepState,
  workflowCompleted,
  analysisMode,
  state,
  actions,
}: UseStep4Input): Step4BodyProps {
  const status = stepState?.status ?? 'empty';
  const hasOutput = HAS_OUTPUT.has(status);

  /* ----- Threats fetch ----- */

  const fetchThreats = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractThreats = useCallback((raw: AgentResultResponse | null): unknown[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.threats_identified ?? r?.threats) as unknown[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const structured = useStructuredFetch<AgentResultResponse | null, unknown[]>({
    fetcher: fetchThreats,
    extract: extractThreats,
    enabled: hasOutput && !!threadId,
  });
  useRefetchOnReviseComplete(status, structured.refetch);

  /* ----- Modal option sources: assets + damage scenarios -----
   * Only fetched when the form is editable; both feed FormModal
   * dropdowns (asset_id select + damage_ids multi-select). */

  const editable = workflowCompleted || status === 'draft';

  const fetchAssets = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, ASSET_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractAssets = useCallback((raw: AgentResultResponse | null): AssetLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.assets ?? r?.identified_assets) as AssetLite[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const assetsFetch = useStructuredFetch<AgentResultResponse | null, AssetLite[]>({
    fetcher: fetchAssets,
    extract: extractAssets,
    enabled: editable && !!threadId,
  });

  const assetOptions: OptionLite[] = (assetsFetch.data ?? []).map((a) => ({
    value: String(a.asset_id ?? ''),
    label: a.asset_name ? `${a.asset_id} — ${a.asset_name}` : String(a.asset_id ?? ''),
  }));

  const fetchScenarios = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, SCENARIO_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractScenarios = useCallback((raw: AgentResultResponse | null): ScenarioLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.damage_scenarios ?? r?.scenarios) as ScenarioLite[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const scenariosFetch = useStructuredFetch<AgentResultResponse | null, ScenarioLite[]>({
    fetcher: fetchScenarios,
    extract: extractScenarios,
    enabled: editable && !!threadId,
  });

  // Damage scenarios can collide on damage_id across child TARAs.
  // Dedupe by damage_id for the dropdown so the user doesn't see
  // duplicate rows (the first label wins, since the first scenario
  // is usually the current-TARA one).
  const scenarioOptions: OptionLite[] = (() => {
    const seen = new Set<string>();
    const out: OptionLite[] = [];
    for (const s of scenariosFetch.data ?? []) {
      const id = String(s.damage_id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const desc = s.damage_scenario
        ? String(s.damage_scenario).slice(0, 60) + (String(s.damage_scenario).length > 60 ? '…' : '')
        : '';
      out.push({ value: id, label: desc ? `${id} — ${desc}` : id });
    }
    return out;
  })();

  /* ----- CRUD ----- */

  const crud = useRecordCRUD({
    threadId,
    agentName: AGENT_NAME,
    enabled: editable,
  });

  /* ----- Hierarchical approve ----- */

  const hierarchicalEnabled = analysisMode === 'hierarchical';
  const records = (structured.data ?? []) as Array<Record<string, unknown>>;
  const hierApprove = useHierarchicalApprove({
    stepId: 4,
    threadId,
    agentName: AGENT_NAME,
    enabled: hierarchicalEnabled,
    records,
    primaryKey: 'threat_id',
    state,
    actions,
    onApproved: structured.refetch,
  });

  return {
    status,
    errorMessage: stepState?.errorMessage ?? null,
    editable,
    structured,
    crud,
    assetOptions,
    scenarioOptions,
    hierarchical: {
      enabled: hierarchicalEnabled,
      modifications: hierApprove.modifications,
      setRelevance: hierApprove.setRelevance,
      setJustification: hierApprove.setJustification,
      onApprove: hierApprove.onApprove,
      approving: hierApprove.approving,
      approveError: hierApprove.approveError,
    },
    approveOverride: hierarchicalEnabled
      ? { onApprove: hierApprove.onApprove, approving: hierApprove.approving }
      : undefined,
  };
}
